/**
 * The on/off confirmation as plain data (ACR-1595, ACR-1599): it lists the flags,
 * shows the environment, and says that clients with their own override keep it,
 * for turn-on and turn-off alike.
 */

export interface ConfirmFlag {
    readonly key: string;
    readonly name: string;
    readonly archived: boolean;
}

export interface ConfirmFlagRow {
    readonly key: string;
    readonly name: string;
    readonly note?: string;
}

export interface ConfirmView {
    readonly title: string;
    readonly confirmLabel: string;
    readonly summary: string;
    readonly flags: readonly ConfirmFlagRow[];
    readonly environmentLabel: string;
    readonly overrideNotice: string;
    /** Specific clients only: "3 clients". */
    readonly clientsLabel?: string;
    /** Specific clients, turn-off only: the lower-bound overwrite warning. */
    readonly overwriteLabel?: string;
}

/** The clients overwritten by a turn-off, as the chunked preview summed them. */
export interface OverwriteEstimate {
    readonly count: number;
    readonly incomplete: boolean;
}

function clientsPhrase(count: number): string {
    return `${String(count)} ${count === 1 ? 'client' : 'clients'}`;
}

function overwriteLabel(estimate: OverwriteEstimate): string {
    const verb = estimate.count === 1 ? 'holds' : 'hold';
    const base = `At least ${clientsPhrase(estimate.count)} ${verb} an override that this will overwrite.`;
    return estimate.incomplete ? `${base} Some clients could not be checked.` : base;
}

function flagsSummary(flags: readonly ConfirmFlag[]): string {
    return `${String(flags.length)} ${flags.length === 1 ? 'flag' : 'flags'}`;
}

function flagRows(flags: readonly ConfirmFlag[]): ConfirmFlagRow[] {
    return flags.map((flag) => ({
        key: flag.key,
        name: flag.name,
        ...(flag.archived ? { note: 'Archived: it will be skipped' } : {}),
    }));
}

export function confirmView(input: {
    readonly releaseName: string;
    readonly enabled: boolean;
    readonly flags: readonly ConfirmFlag[];
    readonly environment: string;
    /** Defaults to all clients. */
    readonly scope?: 'all' | 'clients';
    /** Required for scope `clients`. */
    readonly clientCount?: number;
    /** Specific clients, turn-off: the preview result. Shown as a lower bound. */
    readonly overwrite?: OverwriteEstimate;
}): ConfirmView {
    const verb = input.enabled ? 'on' : 'off';
    if (input.scope === 'clients') {
        const clients = clientsPhrase(input.clientCount ?? 0);
        return {
            title: `Turn ${verb} ${input.releaseName} for ${clients}?`,
            confirmLabel: `Turn ${verb} for ${clients}`,
            summary: flagsSummary(input.flags),
            flags: flagRows(input.flags),
            environmentLabel: `Environment: ${input.environment}`,
            overrideNotice:
                `Each selected client gets its own override on every flag, replacing any override it already has, including a manual one. ${
                    input.enabled ? 'Any rollout percentage is removed.' : ''
                }`.trim(),
            clientsLabel: clients,
            ...(!input.enabled && input.overwrite !== undefined
                ? { overwriteLabel: overwriteLabel(input.overwrite) }
                : {}),
        };
    }
    return {
        title: `Turn ${verb} ${input.releaseName} for all clients?`,
        confirmLabel: `Turn ${verb} for all clients`,
        summary: flagsSummary(input.flags),
        flags: flagRows(input.flags),
        environmentLabel: `Environment: ${input.environment}`,
        overrideNotice:
            'This changes the platform default of each flag. Clients with their own override keep it.',
    };
}

/**
 * Which on/off buttons the page offers. Each scope follows its route's tier: all
 * clients is engineer only (`canManage`), specific clients is ops-csm and above
 * (`canManageClients`); each must match its route's `@RequiresRole`. Turn-off is
 * offered like turn-on, behind the typed phrase (ACR-1598). There is no cancel.
 */
export function availableOperationActions(input: {
    readonly canManage: boolean;
    readonly canManageClients: boolean;
    readonly hasFlags: boolean;
    /** An operation holds the release. */
    readonly locked: boolean;
}): {
    readonly turnOn: boolean;
    readonly turnOnClients: boolean;
    readonly turnOffAll: boolean;
    readonly turnOffClients: boolean;
} {
    const free = input.hasFlags && !input.locked;
    return {
        turnOn: input.canManage && free,
        turnOnClients: input.canManageClients && free,
        turnOffAll: input.canManage && free,
        turnOffClients: input.canManageClients && free,
    };
}
